"use client"

import useSWR from 'swr'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { soundsApi, LIST_COLUMNS, type Sound } from '@/lib/supabase'

// Page size — 24 per page for fast LCP; users paginate to see more
export const PAGE_SIZE = 24

export interface SoundFilters {
  category?: string
  search?: string
  sortBy?: 'created_at' | 'downloads' | 'title' | 'duration'
  sortOrder?: 'asc' | 'desc'
  collectionId?: string
  teslaModel?: string
  premiumOnly?: boolean
}

interface PaginatedSoundsResult {
  sounds: Sound[]
  isLoading: boolean
  /**
   * True while the current key (filters + offset) is fetching or revalidating.
   * Consumers can wait for it before treating an empty accumulated list as a
   * settled result or marking the current page loaded.
   */
  isValidating: boolean
  error: Error | null
  totalCount: number
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
  nextPage: () => void
  prevPage: () => void
  goToPage: (page: number) => void
  mutate: () => void
}

// Serialize filters to a stable string for SWR key
function serializeFilters(filters: SoundFilters): string {
  return JSON.stringify(filters, Object.keys(filters).sort())
}

// Fetcher function for sounds
async function fetchSoundsPage(
  filters: SoundFilters,
  offset: number
): Promise<Sound[]> {
  const apiOptions: Parameters<typeof soundsApi.getSounds>[0] = {
    limit: PAGE_SIZE,
    offset,
    sortBy: filters.sortBy || 'downloads',
    sortOrder: filters.sortOrder || 'desc',
    columns: LIST_COLUMNS,
  }

  if (filters.category && filters.category !== 'All') {
    apiOptions.category = filters.category
  }

  if (filters.collectionId) {
    return soundsApi.getSoundsByCollection(filters.collectionId)
  }

  if (filters.search) {
    apiOptions.search = filters.search
  }

  if (filters.teslaModel) {
    apiOptions.compatibleModels = filters.teslaModel
  }

  if (filters.premiumOnly) {
    apiOptions.premiumOnly = true
  }

  return soundsApi.getSounds(apiOptions)
}

// Fetcher function for total count
async function fetchTotalCount(filters: SoundFilters): Promise<number> {
  // Collection doesn't support count in the same way
  if (filters.collectionId) {
    const sounds = await soundsApi.getSoundsByCollection(filters.collectionId)
    return sounds.length
  }

  return soundsApi.getSoundsCount({
    category: filters.category,
    search: filters.search,
    compatibleModels: filters.teslaModel,
    premiumOnly: filters.premiumOnly,
  })
}

export interface UsePaginatedSoundsOptions {
  /** Seed page from a `?page=` URL param on first render (back-nav restore). */
  initialPage?: number
  /**
   * Called whenever currentPage settles on a new value (nextPage/prevPage/
   * goToPage, or a filter change resetting to page 1) so the caller can
   * mirror it into the URL — mirrors SearchFilters.updateURL's
   * router.push(..., { scroll: false }) pattern. Not called on first mount
   * (the initial value came from the URL already, re-pushing it would add a
   * redundant history entry).
   */
  onPageChange?: (page: number) => void
}

export function usePaginatedSounds(
  filters: SoundFilters,
  initialData?: { sounds?: Sound[]; count?: number },
  options: UsePaginatedSoundsOptions = {},
): PaginatedSoundsResult {
  const { initialPage = 1, onPageChange } = options

  // Create a stable key based on filters
  const filterKey = useMemo(() => serializeFilters(filters), [filters])

  // Track pagination state including the filter key it belongs to
  // When filter key changes, page resets to 1
  const [paginationState, setPaginationState] = useState({
    currentPage: initialPage,
    filterKey: filterKey,
  })

  // Derive the actual current page - if filters changed, show page 1
  const currentPage = paginationState.filterKey === filterKey
    ? paginationState.currentPage
    : 1

  // Mirror currentPage out to the caller (URL) — but only when the page value
  // actually changes. `onPageChange` is typically a useCallback over
  // `searchParams`, so its identity churns on every URL change; keeping it in
  // the dependency array re-pushed the current page for URL changes this hook
  // did not cause, which was half of the /sounds replaceState loop (TES-1872).
  // Seeding the ref with `currentPage` also covers the first run: that value
  // was just read *from* the URL via initialPage, so re-pushing it would add a
  // redundant history entry.
  const onPageChangeRef = useRef(onPageChange)
  useEffect(() => {
    onPageChangeRef.current = onPageChange
  }, [onPageChange])

  const lastMirroredPageRef = useRef(currentPage)
  useEffect(() => {
    if (lastMirroredPageRef.current === currentPage) return
    lastMirroredPageRef.current = currentPage
    onPageChangeRef.current?.(currentPage)
  }, [currentPage])

  // Calculate offset based on current page
  const offset = (currentPage - 1) * PAGE_SIZE

  // Only use server-rendered initialData as fallback when no active filters AND the
  // default sort are applied. The server payload is ordered by downloads/desc; seeding
  // it for any other sort (e.g. "Newest") would pin the grid to the wrong order and
  // make the sort dropdown appear to do nothing.
  const isDefaultSort =
    (!filters.sortBy || filters.sortBy === 'downloads') &&
    (!filters.sortOrder || filters.sortOrder === 'desc')
  const isDefaultFilters =
    !filters.search &&
    (!filters.category || filters.category === 'All') &&
    !filters.teslaModel &&
    !filters.collectionId &&
    !filters.premiumOnly &&
    isDefaultSort

  // Fetch sounds for current page
  const {
    data: sounds,
    error: soundsError,
    isLoading: soundsLoading,
    isValidating: soundsValidating,
    mutate: mutateSounds,
  } = useSWR(
    { type: 'sounds', filterKey, offset },
    async (key) => {
      const parsedFilters = JSON.parse(key.filterKey) as SoundFilters
      return fetchSoundsPage(parsedFilters, key.offset)
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
      dedupingInterval: 5000,
      fallbackData: isDefaultFilters && offset === 0 ? initialData?.sounds : undefined,
    }
  )

  // Fetch total count (separate query, cached)
  const {
    data: totalCount,
    error: countError,
    mutate: mutateCount,
  } = useSWR(
    { type: 'count', filterKey },
    async (key) => {
      const parsedFilters = JSON.parse(key.filterKey) as SoundFilters
      return fetchTotalCount(parsedFilters)
    },
    {
      revalidateOnFocus: false,
      keepPreviousData: false,
      dedupingInterval: 10000, // Cache count longer
      fallbackData: isDefaultFilters ? initialData?.count : undefined,
    }
  )

  // Calculate pagination values
  const total = totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  // Navigation functions - always update with current filterKey
  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setPaginationState((prev) => ({
        filterKey,
        currentPage: prev.filterKey === filterKey ? prev.currentPage + 1 : 2,
      }))
    }
  }, [hasNextPage, filterKey])

  const prevPage = useCallback(() => {
    if (hasPrevPage) {
      setPaginationState((prev) => ({
        filterKey,
        currentPage: prev.filterKey === filterKey ? prev.currentPage - 1 : 1,
      }))
    }
  }, [hasPrevPage, filterKey])

  const goToPage = useCallback(
    (page: number) => {
      const validPage = Math.max(1, Math.min(page, totalPages))
      setPaginationState({
        filterKey,
        currentPage: validPage,
      })
    },
    [totalPages, filterKey]
  )

  // Combined mutate function
  const mutate = useCallback(() => {
    mutateSounds()
    mutateCount()
  }, [mutateSounds, mutateCount])

  return {
    sounds: sounds ?? [],
    isLoading: soundsLoading && !sounds,
    isValidating: soundsValidating,
    error: soundsError || countError || null,
    totalCount: total,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    nextPage,
    prevPage,
    goToPage,
    mutate,
  }
}
